import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { EcrDeploymentsContext } from '../contexts/EcrDeploymentsContext';

interface TriggerDeploymentsModalProps {
  open: boolean;
  onClose: () => void;
}

const TriggerDeploymentsModal: React.FC<TriggerDeploymentsModalProps> = ({ open, onClose }) => {
  const { triggerDeployments } = useContext(EcrDeploymentsContext);
  const [triggering, setTriggering] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const handleTrigger = async () => {
    setError('');
    setResult(null);
    try {
      setTriggering(true);
      const response = await triggerDeployments();
      setResult(response);
    } catch (err: any) {
      setError(err.message || 'Failed to trigger deployments');
    } finally {
      setTriggering(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RocketLaunchIcon color="primary" />
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Trigger Deployments
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Send deployment commands to all machines with active assignments
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!result && !triggering && (
            <Alert severity="info">
              <Typography variant="body2">
                This will send deployment commands to all machines that have active pending deployments.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Machines will process the deployments in the background and report back when complete.
              </Typography>
            </Alert>
          )}

          {triggering && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Triggering deployments...
              </Typography>
            </Box>
          )}

          {result && (
            <Box>
              <Box display="flex" gap={2} mb={2}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${result.successful || 0} Successful`}
                  color="success"
                  variant="outlined"
                />
                {result.failed > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${result.failed} Failed`}
                    color="error"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={`${result.machines_processed || 0} Machines Processed`}
                  variant="outlined"
                />
              </Box>

              <Alert severity={result.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {result.success
                    ? 'Deployments triggered successfully!'
                    : 'Some deployments failed to trigger'}
                </Typography>
                <Typography variant="caption">
                  {result.timestamp && `Triggered at: ${new Date(result.timestamp).toLocaleString()}`}
                </Typography>
              </Alert>

              {result.results && result.results.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Machine Results
                  </Typography>
                  <List dense>
                    {result.results.map((machineResult: any, index: number) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight={600}>
                                  {machineResult.machine}
                                </Typography>
                                <Chip
                                  label={machineResult.status}
                                  size="small"
                                  color={machineResult.status === 'sent' ? 'success' : 'default'}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                  {machineResult.services} service(s) triggered
                                </Typography>
                                {machineResult.response?.response?.message && (
                                  <Typography variant="body2" color="textSecondary">
                                    {machineResult.response.response.message}
                                  </Typography>
                                )}
                                {machineResult.response?.response?.services && (
                                  <Typography variant="caption" color="textSecondary">
                                    Services: {machineResult.response.response.services.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < result.results.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={triggering}>
          Close
        </Button>
        {!result && (
          <Button
            onClick={handleTrigger}
            variant="contained"
            color="primary"
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={20} /> : <RocketLaunchIcon />}
          >
            {triggering ? 'Triggering...' : 'Trigger Deployments'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TriggerDeploymentsModal;
